import { useEffect, useRef, useState } from "react";

import { HubSupportTicketAttachmentList } from "@/components/HubSupportTicketAttachmentList";
import {
  addHubSupportTicketPastedFiles,
  HubSupportTicketAttachmentPicker,
} from "@/components/HubSupportTicketAttachmentPicker";
import { MentionTextarea } from "@/components/MentionTextarea";
import { Button } from "@/components/ui/Button";
import { formatHubSupportTicketStatusLabel } from "@/lib/hubSupportTicketStatus";
import type { HubSupportLocalAttachment, HubSupportTicketAttachmentView } from "@/lib/hubSupportTicketAttachments";
import { cn, trimText } from "@/lib/utils";
import { formatDateTime } from "@/pages/manufacturing/workOrdersPageModel";

export interface HubSupportTicketMessage {
  id: string;
  created_at: string;
  author_user_id: string;
  author_email: string;
  author_display_name: string;
  body: string;
  is_from_reporter: boolean;
  attachments?: HubSupportTicketAttachmentView[];
}

function messageAuthorLabel(message: HubSupportTicketMessage): string {
  const displayName = trimText(message.author_display_name);
  if (displayName !== "") {
    return displayName;
  }
  return message.author_email;
}

export { formatHubSupportTicketStatusLabel };

export function HubSupportTicketMessageThread({
  messages,
  draft,
  onDraftChange,
  onSubmit,
  submitting,
  canReply,
  error,
  composeLabel = "Reply",
  viewer = "reporter",
  staffPartyLabel = "Support",
  allowAttachments = false,
  sectionLabel = "Conversation",
  emptyMessage = "No messages yet. Send a reply to start the thread.",
  placeholder = "Write a message to the support team…",
}: {
  messages: HubSupportTicketMessage[];
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (payload: { attachments: HubSupportLocalAttachment[] }) => void;
  submitting: boolean;
  canReply: boolean;
  error?: string | null;
  composeLabel?: string;
  viewer?: "reporter" | "admin";
  staffPartyLabel?: string;
  allowAttachments?: boolean;
  sectionLabel?: string;
  emptyMessage?: string;
  placeholder?: string;
}): JSX.Element {
  const [pendingAttachments, setPendingAttachments] = useState<HubSupportLocalAttachment[]>([]);
  const wasSubmitting = useRef(false);

  useEffect(() => {
    if (wasSubmitting.current && !submitting && !error) {
      setPendingAttachments([]);
    }
    wasSubmitting.current = submitting;
  }, [submitting, error]);

  const canSend = draft.trim() !== "" || pendingAttachments.length > 0;

  return (
    <div className="space-y-3">
      <span className="block text-xs font-medium uppercase tracking-wide text-slate-500">{sectionLabel}</span>
      <div className="max-h-80 min-h-[6rem] overflow-y-auto rounded-md border border-slate-100 bg-slate-50/60 p-3">
        {messages.length === 0 ? (
          <p className="text-sm text-slate-600">{emptyMessage}</p>
        ) : (
          <ul className="space-y-3">
            {messages.map((message) => (
              <li
                key={message.id}
                className={cn(
                  "rounded-md border px-3 py-2 text-sm",
                  message.is_from_reporter
                    ? "border-slate-200 bg-white"
                    : "border-brand-100 bg-brand-50/70",
                )}
              >
                {message.body.trim() !== "" ? (
                  <p className="whitespace-pre-wrap break-words text-slate-900">{message.body}</p>
                ) : null}
                {message.attachments && message.attachments.length > 0 ? (
                  <div className={message.body.trim() !== "" ? "mt-2" : undefined}>
                    <HubSupportTicketAttachmentList attachments={message.attachments} />
                  </div>
                ) : null}
                <p className="mt-1 text-xs text-slate-500">
                  {viewer === "reporter" && message.is_from_reporter
                    ? "You"
                    : message.is_from_reporter
                      ? messageAuthorLabel(message)
                      : `${staffPartyLabel} · ${messageAuthorLabel(message)}`}
                  <span className="text-slate-400"> · </span>
                  {formatDateTime(message.created_at) || message.created_at}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
      {canReply ? (
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSend) return;
            onSubmit({ attachments: pendingAttachments });
          }}
          onPaste={async (event) => {
            if (!allowAttachments) return;
            const added = await addHubSupportTicketPastedFiles(
              event.clipboardData?.items,
              pendingAttachments,
              setPendingAttachments,
            );
            if (added) event.preventDefault();
          }}
        >
          <label className="sr-only" htmlFor="hub-support-ticket-reply">
            {composeLabel}
          </label>
          <MentionTextarea
            id="hub-support-ticket-reply"
            rows={3}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            value={draft}
            onChange={onDraftChange}
            placeholder={placeholder}
          />
          {allowAttachments ? (
            <HubSupportTicketAttachmentPicker
              attachments={pendingAttachments}
              onChange={setPendingAttachments}
              disabled={submitting}
            />
          ) : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={submitting || !canSend}>
              {submitting ? "Sending…" : composeLabel}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

export function HubSupportTicketStatusBadge({ status }: { status: string }): JSX.Element {
  const label = formatHubSupportTicketStatusLabel(status);
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ring-inset",
        status === "open" && "bg-sky-50 text-sky-800 ring-sky-200",
        status === "planned" && "bg-violet-50 text-violet-800 ring-violet-200",
        status === "in_progress" && "bg-amber-50 text-amber-900 ring-amber-200",
        status === "pr_submitted" && "bg-indigo-50 text-indigo-900 ring-indigo-200",
        status === "pending_verification" && "bg-cyan-50 text-cyan-900 ring-cyan-200",
        status === "resolved" && "bg-emerald-50 text-emerald-800 ring-emerald-200",
        status === "declined" && "bg-slate-100 text-slate-700 ring-slate-200",
        ![
          "open",
          "planned",
          "in_progress",
          "pr_submitted",
          "pending_verification",
          "resolved",
          "declined",
        ].includes(status) && "bg-slate-100 text-slate-700 ring-slate-200",
      )}
    >
      {label}
    </span>
  );
}
